import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useIsDesktop } from "@/hooks/use-media-query";
import { useBackButtonClose } from "@/hooks/use-back-button-close";
import { cn } from "@/lib/utils";

export interface DialogSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional title shown in a header. Omit for headless mode. */
  title?: string;
  /** Optional description shown below the title. */
  description?: string;
  children: React.ReactNode;
  /** Extra classes applied to the DialogContent wrapper on desktop. */
  dialogClassName?: string;
  /** Extra classes applied to the SheetContent wrapper on mobile. */
  sheetClassName?: string;
}

/**
 * A responsive overlay that renders as a centered Dialog on desktop
 * and a fullscreen slide-out Sheet on mobile.
 *
 * On mobile, pressing the browser back button will close the sheet.
 *
 * Pass `dialogClassName` to control the dialog size on desktop (e.g.
 * `"sm:max-w-4xl w-[90vw] h-[85vh]"` for a large modal).
 */
export function DialogSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  dialogClassName,
  sheetClassName,
}: DialogSheetProps) {
  const isDesktop = useIsDesktop();

  // On mobile, integrate with the browser back button.
  useBackButtonClose(open && !isDesktop, () => onOpenChange(false));

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            "max-h-[85vh] flex flex-col overflow-hidden p-0 gap-0 [&>[data-slot=dialog-close]]:top-2.5 [&>[data-slot=dialog-close]]:right-3",
            dialogClassName,
          )}
          showCloseButton={!title}
        >
          {title && (
            <DialogHeader className="shrink-0 px-6 pt-6 pb-0">
              <DialogTitle>{title}</DialogTitle>
              {description && (
                <DialogDescription>{description}</DialogDescription>
              )}
            </DialogHeader>
          )}
          <div className="flex-1 min-h-0 flex flex-col">{children}</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          "w-full sm:max-w-full p-0 gap-0 [&>button]:hidden",
          sheetClassName,
        )}
        showCloseButton={false}
      >
        {title && (
          <SheetHeader className="shrink-0 px-4 pt-4 pb-0">
            <SheetTitle>{title}</SheetTitle>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
        )}
        <div className="flex-1 min-h-0 flex flex-col">{children}</div>
      </SheetContent>
    </Sheet>
  );
}
